<template>
  <div>
    <div class="title mb-5">
      <v-icon left color="blue darken-2">far fa-credit-card-front</v-icon>
      Using a New Card
    </div>
    <v-alert type="error" text v-model="hasErr" dismissible>
      <div class="subtitle-2">{{ errTitle || "ERROR" }}</div>
      <div>
        {{ errMsg }}
      </div>
    </v-alert>
    <v-form ref="form" v-model="valid" lazy-validation>
      <v-row no-gutters>
        <v-col cols="12">
          <div class="overline">Card Holder Name</div>
          <v-text-field
            outlined
            dense
            v-model="cardHolder"
            maxlength="50"
            placeholder="Please provide name on card"
            :rules="[requireRule]"
            @input="onHolderInput"
          ></v-text-field>
        </v-col>
        <v-col cols="12">
          <div class="overline">Card Number</div>
          <v-text-field
            outlined
            dense
            v-model="cardNo"
            placeholder="Please provide card number"
            v-mask="'#### - #### - #### - ####'"
            :rules="[requireRule, ruleCreditNo]"
          ></v-text-field>
        </v-col>
        <v-col
          cols="12"
          md="6"
          :class="{ 'pr-2': $vuetify.breakpoint.mdAndUp }"
        >
          <div class="overline">Expiration (MM/YYYY)</div>
          <v-text-field
            outlined
            dense
            :placeholder="`e.g. 02/2025`"
            v-model="cardExpires"
            maxlength="7"
            v-mask="'##/####'"
            :rules="[requireRule, ruleExpires]"
          ></v-text-field>
        </v-col>
        <v-col
          cols="12"
          md="6"
          :class="{ 'pl-2': $vuetify.breakpoint.mdAndUp }"
        >
          <div class="overline">Security Code</div>
          <v-text-field
            outlined
            dense
            :placeholder="`e.g. 123`"
            v-model="cardSecure"
            maxlength="3"
            v-mask="'###'"
            append-icon="far fa-question-circle"
            @click:append="toggleHint"
            :rules="[requireRule]"
          ></v-text-field>
        </v-col>
        <v-col cols="12">
          <v-checkbox
            class="mt-0"
            v-model="addMode"
            dense
            :label="`Save card to account`"
            :hint="`You can manage your cards from profile menu`"
            persistent-hint
          ></v-checkbox>
        </v-col>
      </v-row>
      <template v-if="hintSecurityCode">
        <v-alert color="info" text class="mt-4">
          <div class="subtitle-1 font-weight-bold">What is security code?</div>
          <div class="body-2">
            3 digits number on the back of your credit card
            <div class="pt-2">
              <v-img
                src="~@/assets/imgs/secure-code.png"
                max-width="100"
                contain
              ></v-img>
            </div>
          </div>
        </v-alert>
      </template>

      <!-- <div class="mt-4">
        <div class="d-flex">
          <v-btn @click="cancelAdd" text color="primary" min-width="100">
            <v-icon left>far fa-chevron-left</v-icon>
            Back
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            dark
            color="blue darken-2"
            min-width="120"
            depressed
            @click="submitForm"
          >
            <v-icon left>far fa-save</v-icon>
            {{ addMode ? "Save Card" : "OK" }}
          </v-btn>
        </div>
      </div> -->
    </v-form>
  </div>
</template>

<script>
import creditCardType, {
  getTypeInfo,
  types as CardType,
} from "credit-card-type";
import omiseService, { createToken } from "../services/omiseService";

export default {
  data: () => ({
    addMode: false,
    hintSecurityCode: false,
    cardHolder: "",
    cardNo: "",
    cardExpires: "",
    cardSecure: "",
    valid: true,
    hasErr: false,
    errTitle: "",
    errMsg: "",
  }),
  computed: {
    requireRule() {
      return (v) => !!v || "This field is required";
    },
    ruleCreditNo() {
      return (v) => {
        let cardNo = v.replace(/-|\s/g, "");
        if (cardNo.length == 16) {
          return true;
        }
        return "Please specific correct card no.";
      };
    },
    ruleExpires() {
      return (v) => {
        if (v.length == 7 && v.indexOf("/") != -1) {
          return true;
        }
        return "e.g. MM/YYYY";
      };
    },
  },
  methods: {
    onHolderInput(ev) {
      this.cardHolder = ev.toUpperCase();
    },
    toggleHint() {
      this.hintSecurityCode = !this.hintSecurityCode;
    },
    showGuideSecureCode() {},
    submitForm() {
      if (this.addMode == true) {
        let card = this.getCardInfo();
        if (card == null) {
          // show error
          return;
        }
        // get omise token and call create customer or attach card
        createToken(card)
          .then((tokenId) => {
            return omiseService.saveMyCard(tokenId);
          })
          .then((resp) => {
            if (resp.data.status && resp.data.result) {
              //
              this.$emit("onSaved");
              this.clear();
            } else {
              // show error can not add
            }
          })
          .catch((err) => {
            if (err.object != null && err.object == "error") {
              this.errTitle = "ERROR: " + err.code;
              this.errMsg = err.message;
            } else {
              this.errTitle = "ERROR";
              this.errMsg = err.toString() || JSON.stringify(err);
            }
            this.hasErr = true;
          })
          .finally(() => {
            // this.overlay = false;
          });
      } else {
        // return only card info
        let card = this.getCardInfo();
        if (card != null) {
          this.$emit("onUsingTempCard", card);
          this.clear();
        }
      }
    },
    getCardInfo() {
      this.$refs.form.validate();

      if (!this.valid) return null;

      let cardNoClean = this.cardNo.replace(/-|\s/g, "");
      let cardType = creditCardType(cardNoClean).filter(function (card) {
        return (
          card.type === CardType.MASTERCARD ||
          card.type === CardType.VISA ||
          card.type == CardType.JCB
        );
      });

      let cardNoText = this.cardNo;
      return {
        holderName: this.cardHolder,
        cardNo: cardNoClean,
        cardNoText: cardNoText,
        expires: this.cardExpires,
        secureCode: this.cardSecure,
        cardType: cardType.length > 0 ? cardType[0].type || null : null,
      };
    },
    cancelAdd() {
      this.clear();
      this.$emit("onCancel");
    },
    clear() {
      this.addMode = false;
      this.valid = false;
      this.cardHolder = "";
      this.cardNo = "";
      this.cardExpires = "";
      this.cardSecure = "";
      this.hasErr = false;
      this.errTitle = "";
      this.errMsg = "";

      this.$refs.form.resetValidation();
    },
    setForm(args, isSave = false) {
      this.cardHolder = args.holderName;
      this.cardNo = args.cardNoText;
      this.cardExpires = args.expires;
      this.cardSecure = args.secureCode;
      this.addMode = isSave;
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .v-input.text-uppercase {
  input {
    text-transform: uppercase;
  }
}
</style>